/*
 * SPDX-FileCopyrightText: © 2023 Schneider Electric
 *
 * SPDX-License-Identifier: LGPL-2.1-only
 */

/* stylelint-disable color-function-notation, no-descending-specificity */
@import url("../shared.css");

:host {
  --qds-block-promo-padding-vertical: 56px;
  --qds-block-promo-padding-horizontal: 56px;
  --qds-main-section-promo-padding-block: 32px; /* There's no token; In figma has the same name as the padding-vertical but with different value; */
  --qds-main-section-promo-border-radius: 16px; /* There's no token; */
  --qds-main-section-promo-min-width: 800px; /* There's no token; */
  --qds-main-section-promo-max-width: 1424px; /* There's no token; */
  --qds-main-section-promo-image-height-standard: 400px; /* There's no token; */
  --qds-main-section-promo-image-width-standard: 700px; /* There's no token; */
  --qds-main-section-promo-image-height-small: 300px; /* There's no token; */
  --qds-main-section-promo-image-height-large: 500px; /* There's no token; */
  --qds-main-section-promo-image-width-large: 800px; /* There's no token; */
  --qds-main-section-hero-title: bold 2rem/2.5rem var(--qds-font-family-brand); /* There is a token in the latest realease but can't be used yet due a badge token problem; */
  --qds-main-section-promo-text: normal 1rem/1.5rem var(--qds-font-family-brand);
  --qds-web-media-button-position-block-end: var(
    --qds-main-section-promo-padding-horizontal
  );
  --qds-web-media-button-position-bottom: var(
    --qds-main-section-promo-padding-vertical
  );
  --qds-main-section-promo-emphasized-background: rgb(
    17 17 17 / 86%
  ); /* There's no token; */

  --qds-main-section-promo-emphasized-background-light: rgb(
    244,
    244,
    244
  ); /* There's no token; */

  width: 100%;
  scroll-snap-align: start;
}

section {
  box-sizing: border-box;
  justify-content: center;
  padding: var(--qds-block-promo-padding-vertical)
    var(--qds-block-promo-padding-horizontal);
  background: var(--qds-theme-main-background);
}

.flex {
  display: flex;
}

.content-area {
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  gap: var(--qds-main-section-gap-children-unrelated);
  width: 100%;
  padding: var(--qds-main-section-promo-padding-vertical)
    var(--qds-main-section-promo-padding-horizontal);
  background: var(--qds-theme-main-subsection-standard-background);
  border-radius: var(--qds-main-section-promo-border-radius);
}

.image {
  object-fit: cover;
}

.body {
  flex-direction: column;
  gap: var(--qds-main-section-gap-children-unrelated);
}

.content {
  flex-direction: column;
  gap: var(--qds-main-section-gap-children-related);
}

.description,
.description-list {
  margin: 0;
  font: var(--qds-main-section-promo-text);
  color: var(--qds-theme-text-emphasized);
}

.tags {
  gap: var(--qds-control-standard-gap-siblings-related);
}

.group {
  gap: var(--qds-control-large-gap-siblings-related);
  flex-wrap: wrap;
}

[hidden] {
  display: none;
}

[data-importance="subdued"] {
  .body {
    align-self: center;
    max-width: 648px;
  }

  .description {
    text-align: center;
  }

  &[data-size="large"] {
    .group {
      gap: var(--qds-control-jumbo-gap-siblings-related);
    }

    .description,
    .description-list {
      font: var(--qds-main-section-hero-text);
    }
  }
}

[data-importance="emphasized"] {
  .content {
    align-items: center;
  }

  .description {
    text-align: center;
  }

  .image {
    position: absolute;
  }
}

/* Mobile (375 - 767px); */
@media (width <= 767px) {
  :host {
    --qds-block-promo-padding-vertical: 24px;
    --qds-block-promo-padding-horizontal: 16px;
    --qds-main-section-promo-min-width: 320px; /* There's no token; */
    --qds-main-section-promo-padding-vertical: 24px; /* There's no token; */
    --qds-main-section-promo-padding-horizontal: 16px; /* There's no token; */
    --qds-main-section-promo-border-radius: 8px; /* There's no token; */
    --qds-main-section-promo-image-height-standard: 185px; /* There's no token; */
    --qds-main-section-hero-title: bold 1.875rem/2rem
      var(--qds-font-family-brand);
  }

  section {
    min-width: var(--qds-main-section-promo-min-width);
  }

  .group {
    flex-flow: column nowrap;
    align-self: stretch;
  }

  .qds-control-group-inline {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  /* SUBDUED */
  [data-importance="subdued"] {
    .body {
      align-self: stretch;
    }

    .content {
      align-items: center;
    }
  }

  /* STANDARD */
  [data-importance="standard"] {
    .content-area {
      align-items: stretch;
    }

    .image,
    qds-web-media {
      aspect-ratio: 4/3;
      flex: 1 0 0;
      height: var(--qds-main-section-promo-image-height-standard);
      min-height: var(--qds-main-section-promo-image-height-standard);
      border-radius: var(--qds-main-section-promo-border-radius);
    }

    .body {
      padding-top: var(--qds-web-block-carousel-indicator, 0);
    }

    .group {
      flex-direction: column;
    }
  }

  /* EMPHASIZED */
  [data-importance="emphasized"] {
    padding: 0;

    .content-area {
      padding: 0;
      min-height: 636px;
      border-radius: 0;
    }

    .body {
      position: relative;
      margin-top: auto;
      margin-bottom: var(--qds-main-section-promo-padding-vertical);
      margin-inline: var(--qds-main-section-promo-padding-horizontal);
      padding: var(--qds-main-section-promo-padding-vertical)
        var(--qds-main-section-promo-padding-horizontal);
      border-radius: var(--qds-main-section-promo-border-radius);
      background-color: var(--qds-main-section-promo-emphasized-background);
    }

    .image {
      height: 636px;
      width: 100%;
    }
  }
}

/* Tablet (768 - 1023px); */
@media (width > 767px) and (width <= 1023px) {
  :host {
    --qds-block-promo-padding-vertical: 24px;
    --qds-block-promo-padding-horizontal: 16px;
    --qds-main-section-promo-min-width: 768px; /* There's no token; */
    --qds-main-section-promo-padding-vertical: 24px; /* There's no token; */
    --qds-main-section-promo-padding-horizontal: 16px; /* There's no token; */
    --qds-main-section-promo-border-radius: 8px; /* There's no token; */
    --qds-main-section-promo-image-height-standard: 440px; /* There's no token; */
    --qds-main-section-hero-title: bold 1.875rem/2rem
      var(--qds-font-family-brand);
  }

  section {
    padding: var(--qds-main-section-promo-padding-horizontal);
  }

  .group {
    flex-direction: row;
  }

  /* SUBDUED */
  [data-importance="subdued"] {
    .content {
      align-items: center;
    }

    .group {
      justify-content: center;
    }
  }

  /* STANDARD */
  [data-importance="standard"] {
    .content-area {
      align-items: stretch;
    }

    .image,
    qds-web-media {
      height: var(--qds-main-section-promo-image-height-standard);
      border-radius: var(--qds-main-section-promo-border-radius);
    }

    .body {
      padding-top: var(--qds-web-block-carousel-indicator, 0);
    }
  }

  /* EMPHASIZED */
  [data-importance="emphasized"] {
    padding: 0;

    .content-area {
      padding: 0;
      min-height: 636px;
      border-radius: 0;
    }

    .body {
      position: relative;
      margin-top: auto;
      margin-bottom: var(--qds-main-section-promo-padding-vertical);
      max-width: 600px;
      padding: var(--qds-main-section-promo-padding-vertical)
        var(--qds-main-section-promo-padding-horizontal);
      border-radius: var(--qds-main-section-promo-border-radius);
      background-color: var(--qds-main-section-promo-emphasized-background);
    }

    .group {
      justify-content: center;
    }

    .image {
      height: 636px;
      width: 100%;
    }
  }
}

/* Desktop (1024 - 1279px) and Large Desktop (+1280px); */
@media (width > 1023px) {
  section {
    min-width: var(--qds-main-section-promo-min-width);
  }

  /* SUBDUED */
  [data-importance="subdued"] {
    .content-area {
      align-items: stretch;
      max-width: var(--qds-main-section-promo-max-width);
    }

    .body {
      width: 100%;
    }

    .content {
      align-items: center;
    }

    .group {
      justify-content: center;
      align-items: flex-start;
    }

    &[data-size="small"] {
      .body {
        flex-direction: row;
        align-self: stretch;
        max-width: 100%;
      }

      .content {
        align-items: flex-start;
        flex: 1 0 0;
        text-align: left;
      }

      .description,
      .description-list {
        text-align: left;
      }

      .group {
        align-items: center;
      }
    }
  }

  /* STANDARD */
  [data-importance="standard"] {
    .content-area {
      flex-direction: row-reverse;
      justify-content: center;
      max-width: var(--qds-main-section-promo-max-width);
    }

    .body {
      width: 50%;
      padding-bottom: var(--qds-web-block-carousel-indicator);
    }

    .image,
    qds-web-media {
      width: 50%;
      height: var(--qds-main-section-promo-image-height-standard);
      border-radius: var(--qds-main-section-promo-border-radius);
    }

    .start {
      flex-direction: row;
    }

    &[data-size="large"] {
      .image {
        height: var(--qds-main-section-promo-image-height-large);
      }
    }

    &[data-size="small"] {
      .content-area {
        padding: 0;
        overflow: hidden;
      }

      .body {
        padding-block: var(--qds-main-section-promo-padding-block);
      }

      .image {
        padding: 0;
        height: var(--qds-main-section-promo-image-height-small);
      }

      .start .image {
        border-start-start-radius: 0;
        border-start-end-radius: 50vh;
        border-end-start-radius: 0;
        border-end-end-radius: 50vh;
      }

      .start .body {
        padding-inline-end: var(--qds-main-section-promo-padding-horizontal);
      }

      .end .image {
        border-start-start-radius: 50vh;
        border-start-end-radius: 0;
        border-end-start-radius: 50vh;
        border-end-end-radius: 0;
      }

      .end .body {
        padding-inline-start: var(--qds-main-section-promo-padding-horizontal);
      }
    }
  }

  /* EMPHASIZED */
  [data-importance="emphasized"] {
    --qds-section-promo-padding-vertical: 32px;
    --qds-section-promo-padding-horizontal: 32px;

    box-sizing: border-box;

    .body {
      position: relative;
      margin-top: auto;
      width: 652px;
      padding: var(--qds-section-promo-padding-vertical)
        var(--qds-section-promo-padding-horizontal);
      border-radius: var(--qds-main-section-promo-border-radius);
      background-color: var(--qds-main-section-promo-emphasized-background);
    }

    .image {
      border-radius: var(--qds-main-section-promo-border-radius);
    }

    &[data-size="standard"] {
      background-color: var(
        --qds-main-section-promo-emphasized-background-light
      );
      min-height: 712px;

      .content-area {
        padding: 0;
        max-width: var(--qds-main-section-promo-max-width);
      }

      .body {
        margin-bottom: var(--qds-main-section-promo-padding-vertical);
      }

      .group {
        justify-content: center;
      }

      .image {
        height: calc(712px - (var(--qds-block-promo-padding-vertical) * 2));
        width: calc(100% - (var(--qds-block-promo-padding-horizontal) * 2));
      }
    }

    &[data-size="large"] {
      padding: 0;
      min-height: 812px;

      .content-area {
        padding: 0;
        border-radius: 0;
      }

      .group {
        justify-content: center;
      }

      .body {
        margin-bottom: var(--qds-main-section-promo-padding-vertical);
      }

      .image {
        width: 100%;
        border-radius: 0;
        height: 812px;
      }
    }

    &[data-size="small"] {
      min-height: 612px;

      .content-area {
        padding: 0;
        max-width: var(--qds-main-section-promo-max-width);
      }

      .body {
        position: relative;
        background-color: var(--qds-theme-main-background);
      }

      .start .body {
        border-start-start-radius: 0;
        border-end-start-radius: 0;
        border-end-end-radius: 0;

        ::before {
          content: "";
          position: absolute;
          top: -32px;
          inset-inline-start: 0;
          height: 32px;
          width: 32px;
          background-color: transparent;
          border-end-start-radius: var(--qds-main-section-promo-border-radius);
          box-shadow: 0 15px 0 0 var(--qds-theme-main-background);
        }

        ::after {
          content: "";
          position: absolute;
          bottom: 0;
          inset-inline-end: -32px;
          height: 32px;
          width: 32px;
          background-color: transparent;
          border-end-start-radius: var(--qds-main-section-promo-border-radius);
          box-shadow: 0 15px 0 0 var(--qds-theme-main-background);
        }
      }

      .end .body {
        border-start-end-radius: 0;
        border-end-start-radius: 0;
        border-end-end-radius: 0;

        ::before {
          content: "";
          position: absolute;
          top: -32px;
          inset-inline-end: 0;
          height: 32px;
          width: 32px;
          background-color: transparent;
          border-end-end-radius: var(--qds-main-section-promo-border-radius);
          box-shadow: 0 15px 0 0 var(--qds-theme-main-background);
        }

        ::after {
          content: "";
          position: absolute;
          bottom: 0;
          inset-inline-start: -32px;
          height: 32px;
          width: 32px;
          background-color: transparent;
          border-end-end-radius: var(--qds-main-section-promo-border-radius);
          box-shadow: 0 15px 0 0 var(--qds-theme-main-background);
        }
      }

      .content {
        align-items: flex-start;
      }

      .description {
        text-align: left;
      }

      .start {
        align-items: flex-start;
      }

      .end {
        align-items: flex-end;
      }

      .image {
        height: calc(612px - (var(--qds-block-promo-padding-vertical) * 2));
        width: calc(100% - (var(--qds-block-promo-padding-horizontal) * 2));
      }
    }
  }
}

@media (width > 1536px) {
  [data-size="standard"] {
    .image,
    qds-web-media {
      width: var(--qds-main-section-promo-image-width-standard);
    }

    &[data-importance="emphasized"] .image {
      max-width: var(--qds-main-section-promo-max-width);
      border-radius: var(--qds-main-section-promo-border-radius);
    }
  }

  [data-size="large"] {
    .image,
    qds-web-media {
      width: var(--qds-main-section-promo-image-width-large);
    }
  }

  [data-size="small"] {
    .image,
    qds-web-media {
      width: var(--qds-main-section-promo-image-width-standard);
    }

    &[data-importance="emphasized"] .image {
      width: 100%;
      max-width: var(--qds-main-section-promo-max-width);
      border-radius: var(--qds-main-section-promo-border-radius);
    }
  }
}

/* animation effect in block-carousel; */
@keyframes fade-in-opacity {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes fade-out-opacity {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}
